import { useState, type ReactNode } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  EmailOutlined,
  MapOutlined,
  MyLocationOutlined,
  LanguageOutlined,
  NotificationsNoneOutlined,
  HistoryOutlined,
  FileDownloadOutlined,
  SecurityOutlined,
  HelpOutlineOutlined,
  ReportProblemOutlined,
  DescriptionOutlined,
  GavelOutlined,
  LogoutOutlined,
  ChevronRight,
  CardMembershipOutlined,
  LockOutlined,
  LightbulbOutlined,
  LightModeOutlined,
  DarkModeOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { clearSearchHistory, requestDataDownload } from '../services/api';
import { getApiLanguage } from '../config/apiLanguage';
import AppShell, { SettingsGroup, SettingsGroupLabel } from '../components/layout/AppShell';
import { PageHeader } from '../components/ui';
import DesignSystem from '../designSystem';
import { useT } from '../i18n/useT';

const tiles = DesignSystem.colors.iconTile;

function SettingRow({
  icon,
  tileColor,
  title,
  subtitle,
  onClick,
  danger = false,
}: {
  icon: ReactNode;
  tileColor?: string;
  title: string;
  subtitle?: string;
  onClick?: () => void;
  danger?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.35,
        width: '100%',
        border: 0,
        borderBottom: `1px solid ${DesignSystem.colors.separator}`,
        background: 'transparent',
        textAlign: 'left',
        px: 1.6,
        py: 1.3,
        cursor: 'pointer',
        font: 'inherit',
        transition: `background-color ${DesignSystem.motion.duration} ${DesignSystem.motion.easing}`,
        '&:last-child': { borderBottom: 0 },
        '&:hover': { backgroundColor: danger ? DesignSystem.colors.errorSoft : DesignSystem.colors.hover },
      }}
    >
      {danger ? (
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: DesignSystem.borderRadius.md,
            bgcolor: DesignSystem.colors.errorSoft,
            color: DesignSystem.colors.error,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            '& svg': { fontSize: 17 },
          }}
        >
          {icon}
        </Box>
      ) : (
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: DesignSystem.borderRadius.md,
            bgcolor: DesignSystem.colors.hover,
            color: DesignSystem.colors.textSecondary,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            '& svg': { fontSize: 17 },
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: '-0.015em',
            color: danger ? DesignSystem.colors.error : DesignSystem.colors.textPrimary,
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mt: 0.15, lineHeight: 1.3 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {!danger && <ChevronRight sx={{ color: DesignSystem.colors.textSecondary, fontSize: 18 }} />}
    </Box>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { preference, mode } = useThemeMode();
  const t = useT();
  const uiLang = getApiLanguage();
  const languageSubtitle = uiLang === 'de' ? t('languageGerman') : t('languageEnglish');
  const appearanceSubtitle =
    preference === 'system'
      ? `${t('appearanceSystem')} · ${mode === 'dark' ? t('appearanceDark') : t('appearanceLight')}`
      : preference === 'dark'
        ? t('appearanceDark')
        : t('appearanceLight');
  const backendUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [clearHistoryDialogOpen, setClearHistoryDialogOpen] = useState(false);
  const [clearHistoryError, setClearHistoryError] = useState<string | null>(null);
  const [downloadMessage, setDownloadMessage] = useState<string | null>(null);

  const handleLogout = async () => {
    setLogoutDialogOpen(false);
    await logout();
    navigate('/?login=1');
  };

  const handleClearHistory = async () => {
    setClearHistoryError(null);
    if (!user?.token) {
      setClearHistoryError(t('settingsNotSignedIn'));
      return;
    }
    try {
      await clearSearchHistory(user.token, getApiLanguage());
      setClearHistoryDialogOpen(false);
    } catch {
      setClearHistoryError(t('settingsClearHistoryFailed'));
    }
  };

  const handleDownloadData = async () => {
    setDownloadMessage(null);
    if (!user?.token) {
      setDownloadMessage(t('settingsNotSignedIn'));
      return;
    }
    try {
      const res = await requestDataDownload(user.token, getApiLanguage());
      setDownloadMessage(res.message || t('settingsDownloadSuccess'));
    } catch {
      setDownloadMessage(t('settingsDownloadFailed'));
    }
  };

  return (
    <AppShell title={t('navSettings')} maxWidth="sm" showCompanion hideTitle>
      <PageHeader title={t('navSettings')} />
      {downloadMessage && (
        <Typography sx={{ mb: 1.5, color: DesignSystem.colors.textSecondary, fontSize: 14 }}>
          {downloadMessage}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <Box>
          <SettingsGroupLabel>{t('settingsGroupAccount')}</SettingsGroupLabel>
          <SettingsGroup>
            {!user?.verifiedEmail && (
              <SettingRow
                icon={<EmailOutlined />}
                tileColor={tiles.blue}
                title={t('settingsConfirmEmail')}
                subtitle={t('settingsConfirmEmailSub')}
                onClick={() => {
                  if (!user?.token) return;
                  window.open(
                    `${backendUrl}/api/v1.1/confirmEmail?token=${encodeURIComponent(user.token)}&language=en`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
              />
            )}
            <SettingRow icon={<MyLocationOutlined />} tileColor={tiles.blue} title={t('settingsLocation')} subtitle={t('settingsLocationSub')} onClick={() => navigate('/settings/location')} />
            <SettingRow icon={<MapOutlined />} tileColor={tiles.green} title={t('settingsNearbyMap')} subtitle={t('settingsNearbyMapSub')} onClick={() => navigate('/map')} />
            <SettingRow icon={<MapOutlined />} tileColor={tiles.teal} title={t('settingsMaps')} subtitle={t('settingsMapsSub')} onClick={() => navigate('/settings/maps')} />
            <SettingRow icon={<LockOutlined />} tileColor={tiles.slate} title={t('settingsChangePassword')} subtitle={t('settingsChangePasswordSub')} onClick={() => navigate('/settings/password')} />
          </SettingsGroup>

          <SettingsGroupLabel>{t('settingsGroupBilling')}</SettingsGroupLabel>
          <SettingsGroup>
            <SettingRow icon={<CardMembershipOutlined />} tileColor={tiles.ink} title={t('subscription')} subtitle={t('settingsSubscriptionSub')} onClick={() => navigate('/settings/subscription')} />
          </SettingsGroup>

          <SettingsGroupLabel>{t('settingsGroupPreferences')}</SettingsGroupLabel>
          <SettingsGroup>
            <SettingRow icon={<LanguageOutlined />} tileColor={tiles.teal} title={t('language')} subtitle={languageSubtitle} onClick={() => navigate('/settings/language')} />
            <SettingRow
              icon={mode === 'dark' ? <DarkModeOutlined /> : <LightModeOutlined />}
              tileColor={tiles.ink}
              title={t('appearance')}
              subtitle={appearanceSubtitle}
              onClick={() => navigate('/settings/appearance')}
            />
            <SettingRow icon={<NotificationsNoneOutlined />} tileColor={tiles.coral} title={t('settingsNotifications')} subtitle={t('settingsNotificationsSub')} onClick={() => navigate('/settings/notifications')} />
            <SettingRow icon={<HistoryOutlined />} tileColor={tiles.slate} title={t('settingsSearchHistory')} subtitle={t('settingsSearchHistorySub')} onClick={() => setClearHistoryDialogOpen(true)} />
          </SettingsGroup>
        </Box>

        <Box>
          <SettingsGroupLabel>{t('settingsGroupDataPrivacy')}</SettingsGroupLabel>
          <SettingsGroup>
            <SettingRow icon={<FileDownloadOutlined />} tileColor={tiles.blue} title={t('settingsDownloadData')} subtitle={t('settingsDownloadDataSub')} onClick={() => void handleDownloadData()} />
            <SettingRow icon={<SecurityOutlined />} tileColor={tiles.ink} title={t('settingsPrivacySecurity')} subtitle={t('settingsPrivacySecuritySub')} onClick={() => navigate('/settings/privacy')} />
          </SettingsGroup>

          <SettingsGroupLabel>{t('settingsGroupSupport')}</SettingsGroupLabel>
          <SettingsGroup>
            <SettingRow icon={<HelpOutlineOutlined />} tileColor={tiles.sky} title={t('companionHelpCenter')} onClick={() => window.open(`${backendUrl}/support`, '_blank')} />
            <SettingRow icon={<ReportProblemOutlined />} tileColor={tiles.orange} title={t('companionReport')} onClick={() => navigate('/settings/report')} />
            <SettingRow icon={<LightbulbOutlined />} tileColor={tiles.teal} title={t('settingsRequest')} onClick={() => navigate('/settings/report?mode=request')} />
          </SettingsGroup>

          <SettingsGroupLabel>{t('settingsGroupLegal')}</SettingsGroupLabel>
          <SettingsGroup>
            <SettingRow icon={<DescriptionOutlined />} tileColor={tiles.slate} title={t('settingsPrivacyPolicy')} onClick={() => window.open(`${backendUrl}/privacypolicy`, '_blank')} />
            <SettingRow icon={<GavelOutlined />} tileColor={tiles.slate} title={t('settingsTermsOfService')} onClick={() => window.open(`${backendUrl}/terms`, '_blank')} />
          </SettingsGroup>

          <SettingsGroup>
            <SettingRow icon={<LogoutOutlined />} title={t('settingsLogout')} danger onClick={() => setLogoutDialogOpen(true)} />
          </SettingsGroup>
        </Box>
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 300, bgcolor: DesignSystem.colors.surface, color: DesignSystem.colors.textPrimary } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t('settingsLogoutTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: DesignSystem.colors.textSecondary }}>{t('settingsLogoutBody')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: DesignSystem.colors.textSecondary }}>{t('cancel')}</Button>
          <Button color="error" variant="contained" onClick={() => void handleLogout()} sx={{ textTransform: 'none' }}>{t('settingsLogout')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearHistoryDialogOpen}
        onClose={() => setClearHistoryDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 300, bgcolor: DesignSystem.colors.surface, color: DesignSystem.colors.textPrimary } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t('settingsClearHistoryTitle')}</DialogTitle>
        <DialogContent>
          {clearHistoryError && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{clearHistoryError}</Typography>}
          <Typography variant="body2" sx={{ color: DesignSystem.colors.textSecondary }}>{t('settingsClearHistoryBody')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClearHistoryDialogOpen(false)} sx={{ color: DesignSystem.colors.textSecondary }}>{t('cancel')}</Button>
          <Button variant="contained" onClick={() => void handleClearHistory()} sx={{ textTransform: 'none', boxShadow: 'none' }}>{t('settingsClearHistory')}</Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
